import { ColumnDef } from '@tanstack/react-table';
import {useEffect, useState} from 'react';
import { Stack } from 'react-bootstrap';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { ButtonData } from '../../../../../components/buttonGroup/ButtonGroup';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import { DateFilterField } from '../../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useToast } from '../../../../../context';
import { useListFiltersInUrl } from '../../../../../hooks';
import { useGetAllChildrenQuery } from '../../../../../services/endpoints/people/child';
import {
  Child,
  Scopes,
  SFCLocation,
  useMimicMutation,
  User
} from '../../../../../services/endpoints/people/user';
import { getCurrentUser } from '../../../../../services/helper';
import { getFilterValue } from '../../../../../utils/listFilterSearchParams';
import { getErrorMessage } from '../../../../../utils/utils';

interface DataRow {
  id: number;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  location: SFCLocation;
  user_type_id: number;
  active: string;
  children?: any[];
  log_in_as: (id: number) => void;
}

const defaultScope = '';

export const userCanEditCreateDelete_Children = (user: User | null) => {
  if (user?.isSuper || user?.isSupremeAdmin) {
    return true;
  }

  return false;
};

export const userCanView_Children = (user: User | null) => {
  if (
      user?.isSuper ||
      user?.isSupremeAdmin ||
      user?.isGlobal ||
      user?.isAdmin ||
      user?.isLocationAdmin
  ) {
    return true;
  }
  return false;
};

export const ChildrenView = () => {
  const { addToast } = useToast();
  const [filters, setFilters] = useListFiltersInUrl({ scope: defaultScope });
  const [scopes, setScopes] = useState<Scopes>({});
  const { data, error, isLoading, isFetching } = useGetAllChildrenQuery(filters);
  const [users, setUsers] = useState<User[]>([]);
  const [buttons, setButtons] = useState<ButtonData[][]>();
  const navigate: NavigateFunction = useNavigate();
  const [mimic] = useMimicMutation();
  const currentUser = getCurrentUser();

  const filterFormFields: FilterField[] = [
    {
      name: 'id',
      label: 'ID',
      type: 'number',
      value: getFilterValue(filters.data?.filterBy, 'id')
    },
    {
      name: 'first_name',
      label: 'First Name',
      value: getFilterValue(filters.data?.filterBy, 'first_name')
    },
    {
      name: 'last_name',
      label: 'Last Name',
      value: getFilterValue(filters.data?.filterBy, 'last_name')
    },
    {
      name: 'gender',
      label: 'Gender',
      value: getFilterValue(filters.data?.filterBy, 'gender')
    },
    {
      name: 'dob_from',
      id: 'dob_formatted',
      operator: 'greater_equal_than',
      element: (
        <DateFilterField
          name="dob_from"
          label="Dob (From-To)"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'dob_formatted', 'greater_equal_than')
    },
    {
      name: 'dob_to',
      id: 'dob_formatted',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="dob_to" />,
      value: getFilterValue(filters.data?.filterBy, 'dob_formatted', 'lesser_equal_than')
    },
    {
      name: 'agree_to_terms',
      label: 'Agree to terms?',
      value: getFilterValue(filters.data?.filterBy, 'agree_to_terms')
    }
  ];

  const columns: ColumnDef<Child>[] = [
    {
      header: 'ID',
      accessorKey: 'id'
    },
    {
      header: 'First Name',
      accessorKey: 'first_name'
    },
    {
      header: 'Last Name',
      accessorKey: 'last_name'
    },
    {
      header: 'Dob',
      accessorKey: 'dob'
    },
    {
      header: () => null,
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => (
        <Stack
          direction="horizontal"
          gap={2}
        >
          <LinkCell
            content="View"
            url={`/admin/sfc-users/children/${row.original.id}`}
            underline
          />
          {userCanEditCreateDelete_Children(currentUser) && (
            <LinkCell
              content="Edit"
              url={`/admin/sfc-users/children/${row.original.id}/edit`}
              underline
            />
          )}
        </Stack>
      )
    }
  ];

  const loginAs = (userId: number) => {
    mimic(userId).then(
      (r) => {
        navigate('/');
      },
      (error: any) => {
        addToast(getErrorMessage(error), 'error');
      }
    );
  };

  useEffect(() => {
    if (!error && !isLoading && data && data.results) {
      if (data.scopes) {
        setScopes(
          data.scopes.reduce((acc: Scopes, curr: Scopes) => ({ ...acc, ...curr }), {} as Scopes)
        );
        setButtons(
          data.scopes.map((sMap) =>
            Object.entries(sMap).map(([scope, title]) => {
              let scope_count = 0;
              if (data.scope_counts != null) {
                scope_count = data.scope_counts[scope] ?? 0;
              }
              return {
                title: `${title} (${scope_count})`,
                key: scope
              };
            })
          )
        );
      }
    } else if (error) {
      addToast('Error while loading data', 'error');
    }
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      const results = data.results.map((user) => ({
        ...user,
        log_in_as: loginAs
      }));
      setUsers(results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  return (
    <div className="py-2">
      <SFCTable
        name="Children"
        columns={columns}
        data={users}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        scopes={buttons}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={filters?.data?.filterBy}
        indexDownloadPath="children"
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        // TODO: add children expanded items
        onFiltersChanged={setFilters}
      />
    </div>
  );
};
